import { ReportField, ReportInfo } from '@shared/providers/salesforce/types';
import { capitalizeString } from '@shared/utils/utils';
import dayjs from 'dayjs';
import Highland from 'highland';
import Stream = Highland.Stream;

const defaultFieldTypes = [
  'number',
  'date',
  'boolean',
  'string',
  'string',
  'string',
  'string',
  'string',
  'string',
  'string',
];

function addDefaultFields(report: GeneratedReport) {
  for (let i = 0; i < defaultFieldTypes.length; i++) {
    const type = defaultFieldTypes[i];
    report.addField(`Object.Field${i + 1}`, `${capitalizeString(type)} Field ${i + 1}`, type);
  }
}

export class GeneratedReport {
  fields: ReportField[] = [];

  constructor(
    public Id: string,
    public Name: string,
    public rowCount: number = 50_000,
  ) {
    addDefaultFields(this);
  }

  addField(fullName: string, name: string, dataType: string) {
    this.fields.push({
      key: fullName,
      fullyQualifiedName: fullName,
      entityColumnName: fullName,
      dataType,
      name,
      label: name,
      type: dataType,
      filterable: false,
      isLookup: false,
      uniqueCountable: false,
    });
  }

  get reportInfo(): ReportInfo {
    return { Name: this.Name, Id: this.Id };
  }

  generateObjectStream(): Stream<any> {
    const generateValue = (rowIndex: number, type: string) => {
      switch (type) {
        case 'number':
          return rowIndex;
        case 'date':
          return dayjs(Date.now()).format('DD/MM/YYYY');
        case 'boolean':
          return rowIndex % 2;
        case 'string':
          return `String value for row ${rowIndex}`;
        default:
          return `Default value for row ${rowIndex}`;
      }
    };
    let rowIndex = 0;

    return Highland((push, next) => {
      if (rowIndex > this.rowCount) {
        push(null, Highland.nil);
        return;
      }

      if (rowIndex === 0) {
        push(null, this.fields.map((field) => field.label));
      } else {
        push(null, this.fields.map((field) => generateValue(rowIndex, field.dataType)));
      }
      rowIndex++;
      next();
    });
  }

  generateDataStream(): Stream<any> {
    const generateValue = (rowIndex: number, type: string) => {
      switch (type) {
        case 'number':
          return rowIndex;
        case 'date':
          return dayjs(Date.now()).format('DD/MM/YYYY');
        case 'boolean':
          return rowIndex % 2;
        case 'string':
          return `String value for row ${rowIndex}`;
        default:
          return `Default value for row ${rowIndex}`;
      }
    };

    let rowIndex = 0;

    return Highland((push, next) => {
      if (rowIndex > this.rowCount) {
        push(null, Highland.nil);
        return;
      }

      if (rowIndex === 0) {
        push(null, this.fields.map((field) => field.label).join(',') + '\n');
      } else {
        push(null, this.fields.map((field) => generateValue(rowIndex, field.dataType)).join(',') + '\n');
      }
      rowIndex++;
      next();
    });
  }
}

const testReport50k = new GeneratedReport('{00000000-0000-0000-0000-000000000000}', 'Generated Report 50k', 50_000);
const testReport150k = new GeneratedReport('{00000000-0000-0000-0000-000000000001}', 'Generated Report 150k', 150_000);
const testReport300k = new GeneratedReport('{00000000-0000-0000-0000-000000000002}', 'Generated Report 300k', 300_000);

export { testReport150k, testReport300k, testReport50k };
